"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Project } from "./types";
import { fontWeights } from "@/utils/fonts";

interface PortfolioCardProps {
  project: Project;
  index: number;
}

export function PortfolioCard({ project, index }: PortfolioCardProps) {
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("down");
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current) {
        setScrollDirection("down");
      } else if (currentScrollY < lastScrollY.current) {
        setScrollDirection("up");
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.div
      initial={index === 0 ? { opacity: 1, y: 0 } : { opacity: 0, y: 200 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.8,
        delay: scrollDirection === "down" ? (index % 4) * 0.1 : 0,
        ease: "easeOut"
      }}
      viewport={{
        once: scrollDirection === "up", // Only animate once when scrolling up
        margin: "500px 0px 100px 0px",
        amount: 0.1
      }}
      style={{ backgroundColor: project.bgColor, borderColor: project.borderColor, zIndex: index }}
      className="mx-auto group rounded-[15px] sm:rounded-[18px] lg:rounded-[20px] overflow-hidden border-2 shadow-sm md:mb-5 lg:mb-10 cursor-pointer transition-shadow"
    >
      {/* Rest of your component remains the same */}
      <Link href={`/portfolio/${project.slug}`} className="block h-full">
        <div className="flex flex-col md:flex-row h-full items-stretch min-h-auto 
                sm:w-[550px] 
                md:w-[715px] 
                lg:w-[970px] 
                xl:w-[1220px] 
                2xl:w-[1236px] 
                p-3 
                tsm-350:p-4 
                sm:p-[20px] 
                lg:p-[35px] 
                xl:p-[50px]  
                gap-0 
                md:gap-[10px] 
                xl:gap-[15px] 
                2xl:gap-[30px]">
          {/* Image Section */}
          <div className="flex-1 w-full flex items-center justify-center md:justify-start relative overflow-hidden rounded-[20px] lg:rounded-[30px] mx-auto md:mx-0">
            <div className="relative w-full h-auto md:h-full mb-3 tsm-350:mb-4 sm:mb-2 md:mb-0 max-w-[400px] md:max-w-[607.75px]">
              {project.mockupImage && (
                <img
                  src={project.mockupImage}
                  alt={project.title}
                  className="w-full h-auto md:h-full object-contain block"
                />
              )}
            </div>
          </div>

          {/* Content Section */}
          <div className="flex-1 flex flex-col justify-center max-w-auto md:max-w-[498.25px] mx-auto md:mx-0">
            <div className="mb-1 sm:mb-2 xl:mb-4">
              <div className="mb-4 xl:mb-6 flex flex-row items-center justify-center md:justify-start gap-2">
                {project.logo && (
                  <Image
                    src={project.logo}
                    alt={`${project.title} logo`}
                    width={150}
                    height={42}
                    className="object-contain h-[25px] w-auto transition-all"
                  />
                )}
                <h3
                  className="text-[16px] lg:text-[18px] text-[#212529] leading-tight transition-colors"
                  style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.bold }}
                >
                  {project.title}
                </h3>
              </div>
              <div className="flex flex-wrap justify-center md:justify-start gap-[2px] lg:gap-1 mb-[2px] lg:mb-1 h-fit">
                {project.tags.map((tag, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-center text-[#4D525D] border-[1px] border-[#4D525D]/25 bg-white rounded-[4px] transition-all
                      py-1 
                      px-3 
                      lg:px-4 
                      xl:px-[18px] 
                      text-[11px] 
                      lg:text-[14px] 
                      h-[20px] 
                      sm:h-[22px] 
                      lg:h-[29px] 
                      xl:text-[15px]"
                    style={{ fontFamily: 'Inter' }}
                  >
                    {tag}
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-1 lg:mb-4 xl:mb-6 text-center md:text-left">
              <h3
                className="inline-block text-[#0A165E] px-2 py-1 sm:text-[18px] md:text-[20px] lg:text-[22px] xl:text-[24px] leading-tight"
                style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.bold }}
              >
                {project.headline}
              </h3>
            </div>

            <p
              className="text-[14px] lg:text-[16px] xl:text-[18px] text-[#585B6F] mb-4 lg:mb-[25px] leading-[1.25] lg:leading-normal xl:leading-relaxed text-center md:text-left"
              style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
            >
              {project.description}
            </p>

            <div className="flex justify-center md:justify-start">
              <span
                className="inline-flex items-center justify-center font-bold transition-all bg-[#002F6D] text-white rounded-[6px] 
                px-4 
                sm:px-[20px] 
                xl:px-[30px] 
                py-2 
                sm:py-[12px] 
                xl:py-[14px] 
                text-[13px] 
                sm:text-[15px] 
                xl:text-[16px]"
                style={{ fontFamily: "Inter", fontWeight: fontWeights.inter.bold, backgroundColor: project.btnColor || '#002F6D' }}
              >
                {project.btnText}
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

interface PortfolioItemProps {
  title?: string;
  headline?: string;
  description?: string;
  tags?: string[];
  logo?: string;
  mockupImage?: string;
  slug?: string;
  bgColor?: string;
  borderColor?: string;
  btnColor?: string;
  btnText?: string;
  index?: number;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}

export function PortfolioItem(props: PortfolioItemProps) {
  const project = {
    title: props.title || "",
    headline: props.headline || "",
    description: props.description || "",
    tags: props.tags || [],
    logo: props.logo || "",
    mockupImage: props.mockupImage || "",
    slug: props.slug || "",
    bgColor: props.bgColor || "#ffffff",
    borderColor: props.borderColor || "#e2e8f0",
    btnColor: props.btnColor || "#002F6D",
    btnText: props.btnText || "View Portfolio"
  };

  const topPadding = props.isFirstInGroup ? "pt-8 sm:pt-12 md:pt-16 lg:pt-20 xl:pt-[100px]" : "pt-0";
  const bottomPadding = props.isLastInGroup ? "pb-8 sm:pb-12 md:pb-16 lg:pb-20 xl:pb-[100px]" : "pb-0";

  return (
    <div className={`flex flex-col px-4 sm:px-6 max-w-fit mx-auto bg-white ${topPadding} ${bottomPadding}`}>
      <PortfolioCard project={project as any} index={props.index || 0} />
    </div>
  );
}